import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { COLORS } from '../../../shared/colors';

const TemplateModal = ({ 
  open, 
  onClose, 
  onSave, 
  template = null, 
  promptTypes = [] 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    content: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        type: template.type,
        content: template.content
      });
    } else {
      setFormData({
        name: '',
        type: promptTypes[0] || '',
        content: ''
      });
    }
    setErrors({});
  }, [template, promptTypes, open]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    
    if (!formData.type) {
      newErrors.type = 'Prompt type is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Template content is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        ...formData,
        ...(template && { id: template.id })
      });
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2
      }}>
        <Box>
          {template ? 'Edit Template' : 'Create New Template'}
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: COLORS.gray[500],
            '&:hover': { bgcolor: COLORS.gray[50] }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 3 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          <TextField
            label="Template Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            variant="outlined"
          />
          
          <FormControl fullWidth error={!!errors.type}>
            <InputLabel>Prompt Type</InputLabel>
            <Select
              value={formData.type}
              onChange={handleChange('type')}
              label="Prompt Type"
            >
              {promptTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Template Content"
            value={formData.content}
            onChange={handleChange('content')}
            error={!!errors.content}
            helperText={errors.content}
            multiline
            rows={8}
            fullWidth
            variant="outlined"
            placeholder="Enter your prompt template content here..."
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: COLORS.gray[600],
            '&:hover': { bgcolor: COLORS.gray[50] }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: COLORS.primary,
            '&:hover': { bgcolor: COLORS.blue[600] }
          }}
        >
          {template ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateModal;